import React, { useState } from 'react';
import { User, Phone, Mail, MessageSquare, MapPin, Clock, CheckCircle2, Loader2, Send, Instagram, Facebook } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { api, buildContactMessage, buildWhatsAppUrl } from '../lib/api';
import { CONTACT } from '../data/mock';
import { trackContactSubmit, trackCallClick, trackWhatsAppClick } from '../lib/analytics';
import { Link } from 'react-router-dom';

const Contact = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: '',
    mobile: '',
    email: '',
    subject: '',
    message: '',
  });

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.mobile.length < 7 || form.message.length < 5) {
      toast({ title: 'Missing details', description: 'Please fill name, mobile, and message.' });
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/contact', form);
      // Track conversion event (GA4 + Meta Pixel)
      trackContactSubmit(form);
      const waUrl = buildWhatsAppUrl(CONTACT.phoneRaw, buildContactMessage(form));
      trackWhatsAppClick('contact_success');
      setSuccess(true);
      window.setTimeout(() => window.open(waUrl, '_blank', 'noopener'), 900);
    } catch (e) {
      toast({
        title: 'Could not submit',
        description: e?.response?.data?.detail || 'Please try again or call us directly.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div>
        <PageHeader title="Message Sent" breadcrumbs={[{ label: 'Contact' }]} />
        <section className="py-20 lg:py-28">
          <div className="max-w-xl mx-auto px-5 lg:px-10 text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#e870b6] to-[#5b3eb5] flex items-center justify-center mx-auto mb-6 shadow-xl">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-serif text-[34px] lg:text-[40px] font-extrabold text-[#1a0b3d] mb-4">
              We&rsquo;ll be in touch soon!
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Thanks {form.name.split(' ')[0]}, your message has been received. We&rsquo;ve also opened a WhatsApp chat so you can reach us instantly.
            </p>
            <Link to="/">
              <Button className="rounded-full bg-[#5b3eb5] hover:bg-[#4a31a3] text-white px-7 py-6 h-auto">
                Back to Home
              </Button>
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Get in Touch" breadcrumbs={[{ label: 'Contact' }]} />

      <section className="py-16 lg:py-20">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-10 grid lg:grid-cols-[1fr_1.3fr] gap-10">
          {/* Contact info */}
          <aside className="space-y-5">
            <InfoCard icon={MapPin} title="Visit Us" content={CONTACT.address} href={CONTACT.mapsLink} hrefLabel="Open in Maps" />
            <InfoCard icon={Phone} title="Call Us" content={CONTACT.phone} href={`tel:${CONTACT.phoneRaw}`} hrefLabel="Tap to call" onLinkClick={() => trackCallClick('contact_info')} />
            <InfoCard icon={Mail} title="Email" content={CONTACT.email} href={`mailto:${CONTACT.email}`} hrefLabel="Send email" />
            <InfoCard icon={Clock} title="Hours" content={CONTACT.hours} />

            <div className="flex gap-3 pt-2">
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-white border border-[#f3d6f5] hover:bg-[#e870b6] hover:border-[#e870b6] flex items-center justify-center transition-colors group">
                <Instagram size={18} className="text-[#5b3eb5] group-hover:text-white" />
              </a>
              <a href={CONTACT.facebook} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-white border border-[#f3d6f5] hover:bg-[#5b3eb5] hover:border-[#5b3eb5] flex items-center justify-center transition-colors group">
                <Facebook size={18} className="text-[#5b3eb5] group-hover:text-white" />
              </a>
            </div>
          </aside>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-7 lg:p-10 shadow-xl border border-slate-100">
            <h3 className="font-serif text-[28px] font-bold text-[#1a0b3d] mb-1">Send us a message</h3>
            <p className="text-slate-500 mb-6 text-[14px]">We typically respond within a few hours.</p>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field icon={User} label="Name *">
                <Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Your full name" required className="h-12" />
              </Field>
              <Field icon={Phone} label="Mobile *">
                <Input type="tel" value={form.mobile} onChange={(e) => update('mobile', e.target.value)} placeholder="+91 9XXXXXXXXX" required className="h-12" />
              </Field>
              <Field icon={Mail} label="Email">
                <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" className="h-12" />
              </Field>
              <Field icon={MessageSquare} label="Subject">
                <Input value={form.subject} onChange={(e) => update('subject', e.target.value)} placeholder="How can we help?" className="h-12" />
              </Field>
              <Field icon={MessageSquare} label="Message *" className="sm:col-span-2">
                <Textarea value={form.message} onChange={(e) => update('message', e.target.value)} placeholder="Tell us a little about your enquiry..." rows={5} required />
              </Field>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="mt-7 w-full sm:w-auto rounded-full bg-[#5b3eb5] hover:bg-[#4a31a3] text-white px-9 py-7 h-auto text-[15px] font-semibold gap-2 shadow-lg hover:shadow-2xl transition-all disabled:opacity-60"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send size={16} /> Send Message</>}
            </Button>
          </form>
        </div>

        {/* Map */}
        <div className="max-w-[1280px] mx-auto px-5 lg:px-10 mt-16">
          <div className="rounded-3xl overflow-hidden shadow-2xl border border-[#f3d6f5]/50 aspect-[16/7] bg-white">
            <iframe
              title="Ichi and Ori Pet Care Location"
              src={CONTACT.mapsEmbed}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

const InfoCard = ({ icon: Icon, title, content, href, hrefLabel, onLinkClick }) => (
  <div className="bg-white rounded-2xl p-6 border border-[#f3d6f5]/60 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex gap-4 items-start">
      <div className="w-11 h-11 rounded-full bg-[#f3d6f5] flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-[#5b3eb5]" />
      </div>
      <div className="flex-1">
        <h4 className="font-bold text-[#1a0b3d] mb-1">{title}</h4>
        <p className="text-slate-600 text-[14px] leading-relaxed">{content}</p>
        {href && (
          <a href={href} onClick={onLinkClick} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="inline-block mt-2 text-[#5b3eb5] text-[13px] font-semibold hover:text-[#e870b6]">
            {hrefLabel} →
          </a>
        )}
      </div>
    </div>
  </div>
);

const Field = ({ icon: Icon, label, children, className = '' }) => (
  <div className={className}>
    <Label className="flex items-center gap-2 text-[13px] font-semibold text-[#1a0b3d] mb-2">
      {Icon && <Icon size={14} className="text-[#5b3eb5]" />} {label}
    </Label>
    {children}
  </div>
);

export default Contact;
